import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PolicyGetResponse } from '@lio/api-types';
import { PolicyTransactionType, PolicyTransactionStatus, InsuranceLineIds } from '@lio/rule-engine';

import * as styles from '@pages/PortalAdmin/GlobalAlertManagement/GlobalAlerts.styles';

import { Dialog } from '@components/dialog';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms/FormSection';

import { getFormFields } from './getFormFields';
import { getFormattedPolicyNumber } from '@utils/policy';
import { PolicyTransaction } from '@models/policyTransaction';
import { fakeLocalMidnightFromTZMidnight, setDateToMidnight } from '@utils/date';
import { useGetAvailablePolicyMonolineInsuranceLines } from '@services/policy/useGetAvailablePolicyMonolineInsuranceLines';
import Loading from '@components/Loading';
import { useCreateLinkedQuoteFromPolicy } from '@services/policy/useCreateLinkedQuoteFromPolicy';
import { useProductOfferings } from '@services/productOffering/useProductOfferings';
import { addDays, addYears, isAfter, isBefore, isValid, subDays } from 'date-fns';
import { DebugInformationButtonModal } from '@components/debugModeControls/DebugInformationButtonModal';

type CreateLinkedQuoteFromPolicyDialogProps = {
  onClose: () => void;
  policy: PolicyGetResponse;
  isOpen: boolean;
  policyTransactions: PolicyTransaction[];
};

export const CreateLinkedQuoteFromPolicyDialog = ({
  onClose,
  policy,
  policyTransactions,
  isOpen,
}: CreateLinkedQuoteFromPolicyDialogProps) => {
  const navigate = useNavigate();

  const brokerageId = policy.boundQuote?.brokerage.id;

  const primaryLocation = policy.boundQuote?.quoteLocations?.find((ql) => ql.insuredLocation.isPrimary);

  const { productOfferings } = useProductOfferings({
    brokerageId,
    insuranceProductId: policy.boundQuote?.offering?.insuranceProduct.id,
    offeringId: policy.boundQuote?.offering.id,
    jurisdictionId: primaryLocation?.insuredLocation.address?.jurisdiction?.id,
  });

  const [defaultEffectiveDate, defaultExpirationDate] = useMemo(() => {
    // default to: expirationDate of most recently Bound NewBusiness or Renewal transaction OR +1year from effective date, whichever is sooner
    const mostRecentProcessedNewBusinessOrRenewalTransaction = policyTransactions
      ?.filter(
        (pt) =>
          pt.status === PolicyTransactionStatus.Processed &&
          (pt.transactionType === PolicyTransactionType.Renewal ||
            pt.transactionType === PolicyTransactionType.NewBusiness),
      )

      .sort((a, b) => {
        if (!a.quote || !b.quote) return 0;
        return new Date(b.quote.expiresOn).getTime() - new Date(a.quote.expiresOn).getTime();
      })[0];

    let _defaultEffectiveDate: Date = new Date();
    let _defaultExpirationDate: null | Date = addYears(new Date(), 1);

    // if policy.effectiveOn is after _defaultEffectiveDate, set _defaultEffectiveDate to policy.effectiveOn
    if (policy.effectiveOn && isAfter(policy.effectiveOn, _defaultEffectiveDate)) {
      _defaultEffectiveDate = policy.effectiveOn;
    }

    _defaultExpirationDate = mostRecentProcessedNewBusinessOrRenewalTransaction?.quote
      ? mostRecentProcessedNewBusinessOrRenewalTransaction.quote.expiresOn
      : new Date(new Date().setFullYear(new Date().getFullYear() + 1));

    // if _defaultExpirationDate is somehow before _defaultEffectiveDate, set _defaultExpirationDate to null and log
    if (_defaultExpirationDate && isBefore(_defaultExpirationDate, _defaultEffectiveDate)) {
      _defaultExpirationDate = null;
      console.warn(
        'CreateLinkedQuoteFromPolicyDialog: _defaultExpirationDate is before _defaultEffectiveDate',
        _defaultEffectiveDate,
        _defaultExpirationDate,
      );
    }

    return [_defaultEffectiveDate, _defaultExpirationDate];
  }, [policy.effectiveOn, policyTransactions]);

  useEffect(() => {
    methods.reset({
      policyEffectiveDate: fakeLocalMidnightFromTZMidnight(defaultEffectiveDate),
      policyExpirationDate: defaultExpirationDate ? fakeLocalMidnightFromTZMidnight(defaultExpirationDate) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEffectiveDate, defaultExpirationDate]);

  const methods = useForm<{
    policyEffectiveDate: Date | null;
    policyExpirationDate: Date | null;
    newPolicyLOI: string;
  }>({
    mode: 'onChange',
    defaultValues: {},
  });

  const { items, isLoading } = useGetAvailablePolicyMonolineInsuranceLines(policy.id, { enabled: true });
  const { createLinkedQuoteFromPolicy, isPending: isCreatingLinkedPolicy } = useCreateLinkedQuoteFromPolicy();

  const handleSubmit = async () => {
    const values = methods.getValues();

    const result = await createLinkedQuoteFromPolicy({
      policyId: policy.id,
      effectiveOn: values.policyEffectiveDate ? setDateToMidnight(values.policyEffectiveDate)?.toISOString() || '' : '',
      expiresOn: values.policyExpirationDate ? setDateToMidnight(values.policyExpirationDate)?.toISOString() || '' : '',
      insuranceLineId: values.newPolicyLOI,
    });

    if (result.quoteId) {
      navigate(`/quote/${result.quoteId}`);
    }
  };

  const newPolicyLOI = methods.watch('newPolicyLOI');
  const isExcess = newPolicyLOI === InsuranceLineIds.ExcessLiability;
  const formStatePolicyEffectiveDate = methods.watch('policyEffectiveDate');
  const formStatePolicyExpirationDate = methods.watch('policyExpirationDate');

  const selectedLOIItem = useMemo(
    () => (newPolicyLOI && items ? (items.find((item) => item.id === newPolicyLOI) ?? null) : null),
    [newPolicyLOI, items],
  );

  const hasDateOverlapError = useMemo(() => {
    if (
      !selectedLOIItem?.isEnabled ||
      !selectedLOIItem?.existingPolicyEffectiveOn ||
      !formStatePolicyEffectiveDate ||
      !formStatePolicyExpirationDate ||
      !isValid(formStatePolicyEffectiveDate) ||
      !isValid(formStatePolicyExpirationDate)
    ) {
      return false;
    }
    const existingStart = selectedLOIItem.existingPolicyEffectiveOn;
    const existingEnd = selectedLOIItem.existingPolicyCanceledOn ?? selectedLOIItem.existingPolicyExpiresOn;
    if (!existingEnd) return false;
    const effectiveDateEST = setDateToMidnight(formStatePolicyEffectiveDate);
    const expirationDateEST = setDateToMidnight(formStatePolicyExpirationDate);
    return isBefore(effectiveDateEST, existingEnd) && isAfter(expirationDateEST, existingStart);
  }, [selectedLOIItem, formStatePolicyEffectiveDate, formStatePolicyExpirationDate]);

  const isFormValid =
    methods.formState.isValid && Object.keys(methods.formState.errors).length === 0 && !hasDateOverlapError;

  const [policyEffectiveDateMin, policyEffectiveDateMax, policyExpiresOnMax, rangeSelectionInfo] = useMemo(() => {
    let _policyEffectiveDateMin,
      _policyEffectiveDateMax,
      _policyExpiresOnMax: Date | null = null;

    let rangeSelectionInfo: {
      isExcess?: boolean;
      policyEffectiveMin?: Record<string, unknown>;
      policyEffectiveMax?: Record<string, unknown>;
      policyExpiresOnMax?: Record<string, unknown>;
    } = {};

    const jurisdictionAvailability = productOfferings?.items[0]?.brokerageOfferingAvailability.jurisdictions[0];

    if (isExcess) {
      rangeSelectionInfo.isExcess = true;
      const policyEffectiveMin1 = jurisdictionAvailability ? jurisdictionAvailability.effectiveOn : null;
      const policyEffectiveMin2 = policy.effectiveOn;

      // _policyEffectiveDateMin is the latest of policyEffectiveMin1 and policyEffectiveMin2
      _policyEffectiveDateMin = new Date(
        Math.max(policyEffectiveMin1 ? policyEffectiveMin1.getTime() : -Infinity, policyEffectiveMin2.getTime()),
      );

      rangeSelectionInfo.policyEffectiveMin = {
        brokerageOffering: policyEffectiveMin1,
        policyEffectiveOn: policyEffectiveMin2,
        latestInRange: _policyEffectiveDateMin,
      };

      const policyEffectiveMax1 = jurisdictionAvailability ? jurisdictionAvailability.effectiveThrough : 0;
      const policyEffectiveMax2 = policy.expiresOn;
      const policyEffectiveMax3 = addDays(new Date(), 120);

      // _policyEffectiveDateMax is the latest of policyEffectiveMax1 and policyEffectiveMax and policyEffectiveMax3
      _policyEffectiveDateMax = new Date(
        Math.max(
          policyEffectiveMax1 ? policyEffectiveMax1.getTime() : 0,
          policyEffectiveMax2 ? policyEffectiveMax2.getTime() : 0,
          policyEffectiveMax3 ? policyEffectiveMax3.getTime() : 0,
        ),
      );

      rangeSelectionInfo.policyEffectiveMax = {
        brokerageOffering: policyEffectiveMax1,
        policyExpiresOn: policyEffectiveMax2,
        oneHundredTwentyDays: policyEffectiveMax3,
        latestInRange: _policyEffectiveDateMax,
      };

      // _policyExpiresOnMax
      _policyExpiresOnMax = policy.expiresOn;

      rangeSelectionInfo.policyExpiresOnMax = {
        policyExpiresOn: policy.expiresOn,
        latestInRange: _policyEffectiveDateMax,
      };
    } else {
      // not excess
      rangeSelectionInfo.isExcess = false;
      _policyEffectiveDateMin = jurisdictionAvailability ? jurisdictionAvailability.effectiveOn : null;

      rangeSelectionInfo.policyEffectiveMin = {
        brokerageOffering: _policyEffectiveDateMin,
        earliestInRange: _policyEffectiveDateMin,
      };

      const policyEffectiveMax1 = jurisdictionAvailability ? jurisdictionAvailability.effectiveThrough : null;
      const policyEffectiveMax2 = addDays(new Date(), 120);

      // _policyEffectiveDateMax is the earliest of policyEffectiveMax1 and policyEffectiveMax2
      _policyEffectiveDateMax = new Date(
        Math.max(
          policyEffectiveMax1 ? policyEffectiveMax1.getTime() : 0,
          policyEffectiveMax2 ? policyEffectiveMax2.getTime() : 0,
        ),
      );

      rangeSelectionInfo.policyEffectiveMax = {
        brokerageOffering: policyEffectiveMax1,
        oneHundredTwentyDays: policyEffectiveMax2,
        earliestInRange: _policyEffectiveDateMax,
      };

      // _policyExpiresOnMax
      rangeSelectionInfo.policyExpiresOnMax = {
        noCap: true,
      };
    }

    let forceMinUpdate = false;
    let forceMaxUpdate = false;

    // if defaultEffectiveDate is NOT IN THE RANGE  = _policyEffectiveDateMin and _policyEffectiveDateMax set forceMinUpdate to true
    if (
      defaultEffectiveDate &&
      _policyEffectiveDateMin &&
      _policyEffectiveDateMax &&
      (isBefore(defaultEffectiveDate, _policyEffectiveDateMin) ||
        isAfter(defaultEffectiveDate, _policyEffectiveDateMax))
    ) {
      console.warn(`CreateLinkedQuoteFromPolicyDialog: defaultEffectiveDate is not in range`, {
        defaultEffectiveDate,
        _policyEffectiveDateMin,
        _policyEffectiveDateMax,
      });

      forceMinUpdate = true;
    }

    // if defaultExpirationDate is NOT IN THE RANGE  = _policyEffectiveDateMin+1 day and _policyExpiresOnMax set forceMaxUpdate to true
    if (
      defaultExpirationDate &&
      _policyEffectiveDateMin &&
      _policyExpiresOnMax &&
      (isBefore(defaultExpirationDate, addDays(_policyEffectiveDateMin, 1)) ||
        isAfter(defaultExpirationDate, _policyExpiresOnMax))
    ) {
      console.warn(`CreateLinkedQuoteFromPolicyDialog: defaultExpirationDate is not in range`, {
        defaultExpirationDate,
        _policyEffectiveDateMin,
        _policyExpiresOnMax,
      });

      forceMaxUpdate = true;
    }

    if (forceMaxUpdate || forceMinUpdate) {
      methods.reset({
        ...methods.getValues(),
        policyEffectiveDate: forceMinUpdate ? null : defaultEffectiveDate,
        policyExpirationDate: forceMaxUpdate ? null : defaultExpirationDate,
      });
    }

    return [_policyEffectiveDateMin, _policyEffectiveDateMax, _policyExpiresOnMax, rangeSelectionInfo];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultEffectiveDate,
    defaultExpirationDate,
    isExcess,
    policy.effectiveOn,
    policy.expiresOn,
    productOfferings?.items,
  ]);

  const noAvailableLOIs = !isLoading && (!items || items.length === 0 || items.every((item) => !item.isEnabled));

  const handleClose = () => {
    methods.reset({});
    methods.clearErrors();
    onClose();
  };

  const fields = useMemo(() => {
    if (noAvailableLOIs || isLoading) {
      return {};
    }

    let maxPolicyEffectiveDate = policyEffectiveDateMax;

    if (formStatePolicyExpirationDate && isValid(formStatePolicyExpirationDate)) {
      maxPolicyEffectiveDate = isBefore(formStatePolicyExpirationDate, policyEffectiveDateMax)
        ? subDays(formStatePolicyExpirationDate, 1)
        : policyEffectiveDateMax;
    }

    return getFormFields({
      newPolicyLOI: { items: items ?? [] },
      policyEffectiveDate: {
        minDate: policyEffectiveDateMin ?? undefined,
        maxDate: maxPolicyEffectiveDate ?? undefined,
        overlapError: hasDateOverlapError,
      },
      policyExpirationDate: {
        minDate: formStatePolicyEffectiveDate ? addDays(formStatePolicyEffectiveDate, 1) : undefined,
        maxDate: policyExpiresOnMax ?? undefined,
        overlapError: hasDateOverlapError,
      },
    });
  }, [
    noAvailableLOIs,
    isLoading,
    items,
    policyEffectiveDateMin,
    policyEffectiveDateMax,
    policyExpiresOnMax,
    formStatePolicyExpirationDate,
    formStatePolicyEffectiveDate,
    hasDateOverlapError,
  ]);

  const formattedPolicyNumber = getFormattedPolicyNumber(
    policy.policyNumberPrefix,
    policy.policyNumber.policyNumber,
    policy.activePolicyTerm?.termCount,
  );

  return (
    <Dialog
      handleCloseDialog={handleClose}
      isPrimaryBtnDisabled={!isFormValid || isCreatingLinkedPolicy}
      isPrimaryBtnLoading={isCreatingLinkedPolicy}
      open={isOpen}
      primaryBtnAction={handleSubmit}
      primaryBtnText={noAvailableLOIs ? '' : 'Create Linked Quote'}
      secondaryBtnAction={handleClose}
      secondaryBtnText='Exit'
      size='small'
      sx={styles.buttonExtraRightPadding}
      title={`Create Linked Policy to ${formattedPolicyNumber}`}
    >
      <Box
        sx={{
          padding: '0px 28px',
          minHeight: 80,
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {noAvailableLOIs ? (
          <Typography fontWeight='normal' sx={{ py: 2 }}>
            There are no other monoline LOIs available to link to this policy.
          </Typography>
        ) : (
          <Form methods={methods} formId='create-linked-policy-modal-form'>
            <Box sx={{ paddingTop: 0, paddingBottom: 0 }}>
              {isLoading ? (
                <Loading />
              ) : (
                <>
                  <Typography variant='caption' display='block' sx={{ mt: 2, mb: 2 }}>
                    This should only be used to add a linked policy to a GL/Package policy. Do not use this to add
                    linked policies to Excess, Accident, or Management Liability policies
                  </Typography>
                  <FormSection
                    fields={fields}
                    styleConfig={{ spacing: 2, alignItems: 'flex-start', marginBottom: 0 }}
                  />
                  <Typography variant='caption' display='inline-block' sx={{ mt: 2, mb: 2 }}>
                    Please double check effective date and expiration date to make sure they encompass the range you
                    desire for this new policy.{' '}
                    <DebugInformationButtonModal
                      hideBehindDebugExtraDetails
                      title='Debug Extra Details: Date range'
                      tooltipText='Show technical date range information'
                      objectSortKeys={false}
                      jsonValue={
                        {
                          rangeSelectionInfo,
                          defaults: {
                            policyEffectiveDate: fakeLocalMidnightFromTZMidnight(defaultEffectiveDate),
                            policyExpirationDate: defaultExpirationDate
                              ? fakeLocalMidnightFromTZMidnight(defaultExpirationDate)
                              : null,
                          },
                          policyEffectiveDatePicker: {
                            min: policyEffectiveDateMin
                              ? fakeLocalMidnightFromTZMidnight(policyEffectiveDateMin)
                              : undefined,
                            max: policyEffectiveDateMax
                              ? fakeLocalMidnightFromTZMidnight(policyEffectiveDateMax)
                              : undefined,
                          },
                          policyExpirationDatePicker: {
                            max: policyExpiresOnMax ? fakeLocalMidnightFromTZMidnight(policyExpiresOnMax) : undefined,
                          },
                        } as Record<string, unknown>
                      }
                    />
                  </Typography>
                </>
              )}
            </Box>
          </Form>
        )}
      </Box>
    </Dialog>
  );
};
